import { z } from "zod";
import { slugSchema, uuidSchema } from "./common.js";
import { SCHEMA_VERSION } from "./version.js";

/**
 * `iamu/config.json` — project manifest.
 *
 * Governs mode dispatch in the MCP server and drift tracking for automatic sync.
 *
 * Encoded as a discriminated union on `cloud_linked` so the two modes cannot
 * mix:
 *   - local-only (`cloud_linked: false`) → cloud fields must be null
 *   - cloud-linked (`cloud_linked: true`) → cloud_project_id + cloud_api_url
 *     must both be present
 */
export const modelTierSchema = z
  .string()
  .min(1)
  .max(128)
  .describe(
    "Model role tier ('fast', 'thorough', 'inherit') or a specific model identifier.",
  );

export const projectModelsConfigSchema = z
  .object({
    walker: modelTierSchema.optional(),
    matcher: modelTierSchema.optional(),
    judge: modelTierSchema.optional(),
  })
  .strict();

export type ProjectModelsConfig = z.infer<typeof projectModelsConfigSchema>;

const baseProjectFields = {
  schema_version: z.literal(SCHEMA_VERSION),
  id: uuidSchema,
  project_slug: slugSchema,
  project_name: z.string().min(1),
  models: projectModelsConfigSchema.optional(),
} as const;

const localOnlyConfig = z.object({
  ...baseProjectFields,
  cloud_linked: z.literal(false),
  cloud_project_id: z.null(),
  cloud_api_url: z.null(),
});

const cloudLinkedConfig = z.object({
  ...baseProjectFields,
  cloud_linked: z.literal(true),
  cloud_project_id: uuidSchema,
  cloud_api_url: z.string().url(),
});

export const projectConfigSchema = z.discriminatedUnion("cloud_linked", [
  localOnlyConfig,
  cloudLinkedConfig,
]);

export type ProjectConfig = z.infer<typeof projectConfigSchema>;

/** Default config emitted by `/iamu:init` (bootstrap_project tool). */
export function defaultConfig(params: {
  id: string;
  projectName: string;
  projectSlug: string;
}): ProjectConfig {
  return {
    schema_version: SCHEMA_VERSION,
    id: params.id,
    project_slug: params.projectSlug,
    project_name: params.projectName,
    cloud_linked: false,
    cloud_project_id: null,
    cloud_api_url: null,
  };
}
